import React, { useEffect, useRef } from 'react';
import '../styles/AiChat.css';

function AiChat() {
  const contentRef = useRef();
  
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    // Store the current ref value to avoid the cleanup warning
    const currentRef = contentRef.current;

    if (currentRef) {
      currentRef.style.opacity = '0';
      currentRef.style.transform = 'translateY(30px)';
      currentRef.style.transition = 'all 0.6s ease';
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  const handleChatClick = (e) => {
    e.preventDefault();
    console.log("🔍 Chat button clicked - starting debugging");
    
    // The debug output shows several SuperOwl elements we can try to interact with
    
    // First try the widget button if it exists
    const superOwlWidgetButton = document.querySelector('.superowl-widget-button');
    if (superOwlWidgetButton) {
      console.log("✓ Found .superowl-widget-button, clicking it", superOwlWidgetButton);
      superOwlWidgetButton.click();
      return;
    }
    
    // We can see from debug that this exists, but maybe it's hidden or not interactive
    // Try to make it visible and active
    const chatWindow = document.querySelector('.superowl-chat-window');
    if (chatWindow) {
      console.log("✓ Found .superowl-chat-window, attempting to show it", chatWindow);
      chatWindow.style.display = 'block';
      chatWindow.style.opacity = '1';
      chatWindow.style.visibility = 'visible';
      chatWindow.style.zIndex = '9999';
      return;
    }
    
    // Try any of the elements we found in the debug output
    const elementIdsToTry = [
      '.superowl-widget-button', 
      '.superowl-chat-window',
      '.superowl-send'
    ];
    
    for (const selector of elementIdsToTry) {
      const element = document.querySelector(selector);
      if (element) {
        console.log(`✓ Found ${selector}, clicking it`, element);
        element.click();
        return;
      }
    }
    
    // If we get here, try to directly call SuperOwl's API based on the elements we saw
    console.log("🔍 Attempting to use direct SuperOwl API access");
    if (window.SuperOwl) {
      console.log("✓ Found SuperOwl object on window");
      if (typeof window.SuperOwl.open === 'function') {
        window.SuperOwl.open();
        return;
      } else {
        console.log("Available SuperOwl methods:", Object.keys(window.SuperOwl));
      }
    }
    
    // Look for global functions that might control the widget
    for (const key in window) {
      if (typeof window[key] === 'function' && 
          (key.toLowerCase().includes('superowl') || 
           key.toLowerCase().includes('chat') || 
           key.toLowerCase().includes('widget'))) {
        console.log(`Found potential function: ${key}, attempting to call it`);
        try {
          window[key]();
        } catch (e) {
          console.error(`Error calling ${key}:`, e);
        }
      }
    }
    
    // If all else fails, try to interact with the iframe
    const superOwlIframe = document.querySelector('iframe');
    if (superOwlIframe) {
      console.log("✓ Found iframe, attempting to interact with it:", superOwlIframe);
      try {
        // Try to make iframe visible if it's hidden
        superOwlIframe.style.display = 'block';
        superOwlIframe.style.opacity = '1';
        superOwlIframe.style.visibility = 'visible';
        superOwlIframe.style.zIndex = '9999';
        superOwlIframe.style.height = '500px';
        superOwlIframe.style.width = '350px';
        superOwlIframe.style.position = 'fixed';
        superOwlIframe.style.bottom = '20px';
        superOwlIframe.style.right = '20px';
      } catch (error) {
        console.log('Error modifying iframe:', error);
      }
    }
    
    // Try to use custom events
    console.log("⚠️ Attempting various custom events");
    document.dispatchEvent(new CustomEvent('superowl:open'));
    document.dispatchEvent(new CustomEvent('superowl-open'));
    document.dispatchEvent(new CustomEvent('chat:open'));
    document.dispatchEvent(new CustomEvent('widget:open'));
    
    console.log("🔍 All attempts completed - check console for errors");
  };

  return (
    <section className="ai-chat" id="ai-chat">
      <div className="container">
        <div className="ai-chat-container">
          <div className="ai-chat-content" ref={contentRef}>
            <h3>TRO AI - Your Personal Yoga Guide</h3>
            <p>Get personalized yoga guidance anytime, anywhere. Our AI understands your practice level, goals, and preferences to provide tailored sequences and advice.</p>
            
            <p>Ask about:</p>
            <ul style={{ margin: "20px 0", paddingLeft: "20px" }}>
              <li>Pose modifications and alignment</li>
              <li>Custom sequence creation</li>
              <li>Breathing techniques</li>
              <li>Injury prevention and recovery</li>
              <li>Meditation and mindfulness</li>
              <li>Practice scheduling and goals</li>
            </ul>
            
            <button className="btn btn-primary" onClick={handleChatClick}>
              Start Chatting Now
            </button>
          </div>
          
          <div className="chat-mockup">
            <div className="chat-header">
              <div className="chat-avatar">AI</div>
              <div>
                <strong>TRO AI Assistant</strong>
                <div style={{ fontSize: "0.8rem", color: "#666" }}>Online</div>
              </div>
            </div>
            
            <div className="chat-message ai">
              Hi! I'm your TRO AI guide. How can I help with your yoga practice today?
            </div>
            
            <div className="chat-message user">
              I'm new to power yoga. Can you suggest a beginner sequence?
            </div>
            
            <div className="chat-message ai">
              Perfect! I'd recommend starting with a 20-minute flow focusing on breath and basic poses. Let's begin with Sun Salutation A...
            </div>
            
            <div className="chat-input">
              <input type="text" placeholder="Ask about yoga, poses, or techniques..." disabled />
              <button onClick={handleChatClick}>Chat Now</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AiChat;
